<!--
  @component NetworkDeployment — the curated getter + aggregator.
  tier:      hyperedge (composes nodes + edges + a transport into
             one mountable surface)
  publishes: NETWORK_DEPLOYMENT_KEY → NetworkDeploymentContext → useNetworkDeployment()

  WHAT THIS IS
  ─────────────────────────────────────────────────────────────────

  The single mount point for the network deployment substrate.
  Replaces both `IO.svelte` AND the long common-chain in
  `ConsumerRoot.svelte` / `AdminRoot.svelte`. Both faces (consumer +
  admin) collapse their outer ring to:

    <NetworkDeployment endpoint="wss://network.liquidefy.io/cmd">
      <ConsumerShell> <Router /> </ConsumerShell>
    </NetworkDeployment>

  Responsibilities (in order):

    1. Construct a `WebSocketCommandPort` against the configured
       endpoint (default `wss://network.liquidefy.io/cmd`).
    2. Mount `DeploymentIO` with that port injected — flips
       `effecting` from false to true the moment the WS connects.
    3. Mount the common portal chain (Topology / Binding /
       Credentials / Observability / Provenance / Lifecycle /
       Approvals) — same chain admin's ScreenOutlet has always
       mounted, now hoisted up.
    4. Mount the two read-mostly edges (LiveTimelineEdge,
       TopologyTruthEdge) so every screen below sees them.
    5. Expose `useNetworkDeployment()` — the curated getter — so
       any screen can read `{ connected, ready, endpoint, ... }`
       without dipping into IO + edges individually.

  CONTRACT DISCIPLINE
  ─────────────────────────────────────────────────────────────────

  This file does NOT touch secrets. The WebSocketCommandPort is a
  transport — it forwards `{op, payload}` envelopes to the server
  at the configured endpoint and receives back result/error/event
  frames. All secrets live server-side, behind WS.

  D0: the WS connects + handshakes; every dispatch returns
  `NOT_WIRED` from the server (handlers land D2+).
  D1: server-side auth lights up at WS handshake.
  D2+: each substrate's banner disappears as its handler lands.
-->
<script lang="ts" module>
  import { getContext } from "svelte";

  export const NETWORK_DEPLOYMENT_KEY: unique symbol = Symbol("network.deployment");

  export interface NetworkDeploymentContext {
    /** True iff the WS connection is currently open + authenticated. */
    readonly connected: boolean;
    /** The configured endpoint URL. */
    readonly endpoint: string;
    /** Last error seen on the transport (auth fail, network drop, etc). */
    readonly lastError: Error | null;
    /** Force a reconnect — used by the admin face after token rotation. */
    reconnect(): void;
  }

  export function useNetworkDeployment(): NetworkDeploymentContext {
    const ctx = getContext<NetworkDeploymentContext | undefined>(
      NETWORK_DEPLOYMENT_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useNetworkDeployment() called outside of a NetworkDeployment tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";

  import DeploymentIO from "../nodes/deployment/DeploymentIO.svelte";
  import TopologyIO from "../nodes/deployment/TopologyIO.svelte";
  import BindingIO from "../nodes/deployment/BindingIO.svelte";
  import CredentialsIO from "../nodes/deployment/CredentialsIO.svelte";
  import ObservabilityIO from "../nodes/deployment/ObservabilityIO.svelte";
  import ProvenanceIO from "../nodes/deployment/ProvenanceIO.svelte";
  import LifecycleIO from "../nodes/deployment/LifecycleIO.svelte";
  import ApprovalsIO from "../nodes/deployment/ApprovalsIO.svelte";

  import LiveTimelineEdge from "../edges/deployment/LiveTimelineEdge.svelte";
  import TopologyTruthEdge from "../edges/deployment/TopologyTruthEdge.svelte";

  // Security lenses — the tenant-relevant cross-cutting chain: client-side
  // input sanitization (ValidationIO) + role-aware gating (GuardIO) + whoami
  // role sync (SecurityRoleSync, needs useDeploymentIO). Mirrors the front of
  // IO.svelte's chain; mounted INSIDE DeploymentIO (innermost) so the whoami
  // dispatch resolves and every screen/wizard below can call useValidationIO().
  // Without this, screens that call useValidationIO() (Storage/Domains/Settings)
  // throw on mount and render blank. The operator-console lenses (audit-trail/
  // vault/attestation/threat/debt/incident) stay admin-only — the tenant face
  // doesn't surface them.
  import SecurityIO from "../nodes/security/SecurityIO.svelte";
  import ValidationIO from "../nodes/security/ValidationIO.svelte";
  import GuardIO from "../nodes/security/GuardIO.svelte";
  import SecurityRoleSync from "../nodes/security/SecurityRoleSync.svelte";

  import {
    createWebSocketCommandPort,
    type WebSocketCommandPort,
  } from "./network-deployment-transport.js";

  import type {
    DeploymentCommandPort,
    DeploymentIOContext,
  } from "../nodes/deployment/DeploymentIO.svelte";
  import { IO_KEY, type IOContext, type IOBundle } from "../IO.svelte";

  interface Props {
    /** WebSocket dispatch endpoint. Default is the production URL;
     *  override for local dev (e.g. `ws://localhost:3500/cmd`) or
     *  staging. */
    endpoint?: string;
    /** Optional bearer-token producer for the WS handshake. The
     *  default reads `network:consumer:auth` JWT from localStorage
     *  (where IdentityIO stores it after magic-link verify). */
    tokenSource?: () => Promise<string | null>;
    children?: Snippet;
  }
  const {
    endpoint = resolveDefaultEndpoint(),
    tokenSource = defaultTokenSource,
    children,
  }: Props = $props();

  /** Default endpoint resolution: production = network.liquidefy.io,
   *  anything localhost = ws://localhost:3500/cmd (matches the dev
   *  server). */
  function resolveDefaultEndpoint(): string {
    if (typeof window === "undefined") return "wss://network.liquidefy.io/cmd";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "ws://localhost:3500/cmd";
    }
    return "wss://network.liquidefy.io/cmd";
  }

  /** Default token source: pull from localStorage where IdentityIO
   *  stores the JWT after a successful magic-link verify. */
  async function defaultTokenSource(): Promise<string | null> {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem("network:consumer:auth");
  }

  // ── Transport state ─────────────────────────────────────────────
  let connected  = $state(false);
  let lastError  = $state<Error | null>(null);

  // Construct the port SYNCHRONOUSLY so DeploymentIO (which captures
  // `command` once on mount via untrack) sees a stable command port
  // on first render. The WS itself opens async — methods are bound
  // and survive the pre-open window. Browser-only: SSR-safe because
  // the WebSocketCommandPort guards its WebSocket construction.
  // endpoint/tokenSource are intentionally captured ONCE — re-
  // creating the port on prop change would tear down WS state.
  // svelte-ignore state_referenced_locally
  const port: WebSocketCommandPort | null =
    typeof window === "undefined" ? null : createWebSocketCommandPort({
      endpoint,
      tokenSource,
      onConnect:    () => { connected = true; lastError = null; },
      onDisconnect: () => { connected = false; },
      onError:      (err) => { lastError = err; },
    });

  // Tear down on unmount.
  $effect(() => () => port?.close());

  const command: DeploymentCommandPort | undefined = port ?? undefined;

  // ── IO_KEY publication ─────────────────────────────────────────
  // ReleasesPanel + other screens call useIO() expecting
  // the same IOContext shape that IO.svelte publishes. The
  // hyperedge replaces IO.svelte's mount, so we have to publish
  // the IO_KEY context ourselves — otherwise useIO() throws.
  let deploymentCtx = $state<DeploymentIOContext | null>(null);

  const EMPTY_HANDLER: ProxyHandler<object> = {
    get(_t, prop) {
      throw new Error(
        `IO.bundle.${String(prop)} accessed before ready. ` +
          `Gate on useIO().ready (or useGlobal().ready upstream).`,
      );
    },
  };
  const EMPTY_DEPLOYMENT = new Proxy({}, EMPTY_HANDLER) as unknown as DeploymentIOContext;

  const ioBundle: IOBundle = $derived({
    deployment: deploymentCtx ?? EMPTY_DEPLOYMENT,
  });
  const ioReady = $derived(deploymentCtx !== null);
  const ioCtx: IOContext = {
    get ready() { return ioReady; },
    get connected() { return connected; },
    get bundle() { return ioBundle; },
  };
  setContext(IO_KEY, ioCtx);

  // ── Curated getter context ──────────────────────────────────────
  setContext(NETWORK_DEPLOYMENT_KEY, {
    get connected() { return connected; },
    get endpoint()  { return endpoint; },
    get lastError() { return lastError; },
    reconnect() {
      port?.reconnect();
    },
  } satisfies NetworkDeploymentContext);
</script>

<!--
  Mount order (matches the admin face's ScreenOutlet + the consumer
  face's ConsumerRoot exactly — those continue to work unchanged
  because this hyperedge produces the same context tree):

    <DeploymentIO command={port}>
      <TopologyIO>
        <BindingIO>
          <CredentialsIO>
            <ObservabilityIO>
              <ProvenanceIO>
                <LifecycleIO>
                  <ApprovalsIO>
                    <LiveTimelineEdge>
                      <TopologyTruthEdge>
                        {@render children?.()}

  The two read-mostly edges sit innermost so every screen below can
  call useLiveTimelineEdge() / useTopologyTruthEdge(). Effecting
  edges (DeployRunEdge, ReleaseLifecycleEdge) + arc-specific
  effecting portals (Rollback / Ephemeral / Release / Traffic /
  Cache / Queue / DatabaseDeploy / Storage / …) mount per-screen
  because their state is project-local.
-->
<DeploymentIO {command} onReady={(c) => (deploymentCtx = c)}>
  <TopologyIO>
    {#snippet children()}
      <BindingIO>
        {#snippet children()}
          <CredentialsIO>
            {#snippet children()}
              <ObservabilityIO>
                {#snippet children()}
                  <ProvenanceIO>
                    {#snippet children()}
                      <LifecycleIO>
                        {#snippet children()}
                          <ApprovalsIO>
                            {#snippet children()}
                              <LiveTimelineEdge>
                                {#snippet children()}
                                  <TopologyTruthEdge>
                                    {#snippet children()}
                                      <SecurityIO>
                                        <ValidationIO>
                                          <GuardIO>
                                            <SecurityRoleSync>
                                              {@render children?.()}
                                            </SecurityRoleSync>
                                          </GuardIO>
                                        </ValidationIO>
                                      </SecurityIO>
                                    {/snippet}
                                  </TopologyTruthEdge>
                                {/snippet}
                              </LiveTimelineEdge>
                            {/snippet}
                          </ApprovalsIO>
                        {/snippet}
                      </LifecycleIO>
                    {/snippet}
                  </ProvenanceIO>
                {/snippet}
              </ObservabilityIO>
            {/snippet}
          </CredentialsIO>
        {/snippet}
      </BindingIO>
    {/snippet}
  </TopologyIO>
</DeploymentIO>
